// Phase 6 Plan 06-07 — Player-facing lesson slot API.
//
// Wire types mirror the DRF serializers shipped in plan 06-05
// (private_classes/serializers.py — LessonSlotDetailSerializer).
//
// Endpoint mounts under /private_classes/ per project urls.py (NOT
// /api/private-classes/ — see Plan 06-05 SUMMARY decision notes).

import api from '../api'

export type LessonSlotStatus = 'active' | 'soft_reserve' | 'released' | 'canceled'
export type LessonLocationType = 'venue' | 'private_location'

export interface LessonLocation {
  type: LessonLocationType
  label: string
  address: string | null
  image: string | null
  timezone: string
}

export interface LessonSlotCoach {
  id: string
  display_name: string
  profile_image: string | null
  rating: number | null
}

export interface LessonSlotListRow {
  id: string
  coach: {
    id: string
    display_name: string
    profile_image: string | null
    rating: number | null
    // Optional coach-card enrichments (Figma 6081:14347). Not yet supplied by
    // the list serializer — the card renders them only when present, so the
    // review count, "top coach" badge, and credential columns light up once the
    // backend includes them.
    review_count?: number | null
    is_top_coach?: boolean
    credentials?: string[]
  }
  start_local: string
  end_local: string
  court_type: 'pickle' | 'tennis' | 'padel'
  venue_id: string | null
  court: LessonSlotCourt | null
  location_type: 'venue' | 'private_court'
  location_label: string
  location?: LessonLocation
  timezone: string
}

export interface LessonSlotVenue {
  id: string
  name: string
  timezone: string
}

export interface LessonSlotCourt {
  id: string
  court_label: string
  court_type: 'pickle' | 'tennis' | 'padel'
}

// Estimated, pre-discount price preview for the detail receipt. Null when the
// venue has no court-rental pricing for this court type (slot not bookable).
// The authoritative, discount-applied breakdown comes from reserveLesson.
export interface LessonSlotPrice {
  coach_fee_cents: number
  court_rental_cents: number
  total_cents: number
}

export interface LessonSlotDetail {
  id: string
  status: LessonSlotStatus
  is_booked: boolean
  duration_minutes: number
  coach: LessonSlotCoach
  venue: LessonSlotVenue | null
  court: LessonSlotCourt | null
  court_type: 'pickle' | 'tennis' | 'padel'
  location_type: 'venue' | 'private_court'
  location_label: string
  location?: LessonLocation
  timezone: string
  // Venue-local ISO with explicit offset, e.g. "2026-06-15T17:00:00-07:00".
  // Never raw UTC — XC-02.
  start_local: string
  end_local: string
  can_book: boolean
  can_join_waitlist: boolean
  price: LessonSlotPrice | null
}

export const getLessonSlot = async (id: string): Promise<LessonSlotDetail> => {
  const res = await api.get<LessonSlotDetail>(`/private_classes/lesson-slots/${id}/`)
  return res.data
}

export const getLessonSlots = async ({
  venue,
  date,
  court_type,
  gender,
  age_range,
  skill_level,
}: {
  venue: string
  date: string
  court_type?: string
  gender?: string
  age_range?: string
  skill_level?: string
}): Promise<LessonSlotListRow[]> => {
  const params: {
    venue: string
    date: string
    court_type?: string
    gender?: string
    age_range?: string
    skill_level?: string
  } = { venue, date }
  if (court_type !== undefined) {
    params.court_type = court_type
  }
  if (gender) params.gender = gender
  if (age_range) params.age_range = age_range
  if (skill_level) params.skill_level = skill_level
  const res = await api.get<{ data: LessonSlotListRow[] }>('/private_classes/lesson-slots/', {
    params,
  })
  return res.data.data
}

export const getCoachLessonSlots = async ({
  coachId,
  date,
  court_type,
}: {
  coachId: string
  date: string
  court_type?: string
}): Promise<LessonSlotListRow[]> => {
  const params: { date: string; court_type?: string } = { date }
  if (court_type !== undefined) {
    params.court_type = court_type
  }
  const res = await api.get<{ data: LessonSlotListRow[] }>(
    `/private_classes/coaches/${coachId}/lesson-slots/`,
    { params },
  )
  return res.data.data
}

export interface CoachLessonSlotsNextDate {
  date: string | null
}

export const getCoachLessonSlotsNextDate = async (
  coachId: string,
): Promise<CoachLessonSlotsNextDate> => {
  const res = await api.get<CoachLessonSlotsNextDate>(
    `/private_classes/coaches/${coachId}/lesson-slots/next-date/`,
  )
  return res.data
}

// Phase 7 Plan 07-08 — Player-facing lesson reservation API.
//
// Wire types mirror the DRF serializers shipped in plan 07-04
// (private_classes/serializers.py — Reserve / LessonBookingReserveResponse).
//
// NOTE: Per 07-04 SUMMARY, the actual mounted URL is
//   POST /private_classes/lessons/reserve/
// (Django mounts `private_classes/`, not `api/private-classes/`.)
// The 07-04 SUMMARY 3-tuple breakdown nests under `breakdown` on success.

export interface LessonPriceBreakdown {
  coach_fee_cents: number
  court_rental_cents: number
  discount_cents: number
  applied_credit_amount: number
  applied_credit_ids: string[]
  platform_cut_cents: number
  coach_take_home_cents: number
  venue_share_cents: number
  total_cents: number
  membership_label: string
}

export type LessonBookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'released'

export interface ReserveLessonResponse {
  booking_id: string
  stripe_client_secret: string
  status: LessonBookingStatus
  breakdown: LessonPriceBreakdown
}

export type ReserveLessonErrorCode =
  | 'slot_just_taken'
  | 'no_court_available'
  | 'travel_buffer_conflict'
  | 'credit_balance_changed'
  | 'payment_setup_failed'

export interface ReserveLessonErrorResponse {
  error: ReserveLessonErrorCode
  detail: string
}

export const reserveLesson = async (
  slotId: string,
): Promise<ReserveLessonResponse> => {
  const res = await api.post<ReserveLessonResponse>(
    '/private_classes/lessons/reserve/',
    { slot_id: slotId },
  )
  return res.data
}

// Phase 7 Plan 07-08 — booking-status read for the success page poll.
// The bookings endpoint is mounted at /private_classes/bookings/<id>/ but
// the existing BookedPrivateClassView there returns a different (legacy)
// shape. The 07-08 success page reads status directly from the reserve
// response and relies on Stripe's redirect callback for confirmation —
// no separate getLessonBooking() shipped here. Documented as Plan 07-08
// scope-out: a future plan can add /private_classes/lesson-bookings/<id>/
// if poll-to-confirmed UX is required.

export interface LessonUpcomingRow {
  type: 'private_lesson'
  id: string
  start_time: string
  duration_minutes: number
  coach: {
    id: string
    display_name: string
    profile_image: string | null
  }
  venue: {
    id: string
    name: string
    timezone: string
    address?: string
    images?: string[]
  }
  court: {
    id: string
    court_label: string
  }
  location?: LessonLocation
}

// GLOBAL (all-venues) upcoming lessons for the authed member — backs the
// lesson rows on /user/events. Without a `venue` param the backend returns
// the aggregate UpcomingLessonRowSerializer shape that matches
// LessonUpcomingRow; with a `venue` param it returns the venue-card shape
// (see getVenueUpcomingLessons).
export const getUpcomingLessonBookings = async (): Promise<LessonUpcomingRow[]> => {
  const res = await api.get<LessonUpcomingRow[]>('/private_classes/lessons/upcoming/')
  return res.data
}

// Coach-side inbox — lessons booked WITH the authed coach. Each row
// surfaces the booker (member) instead of the coach.
export interface CoachIncomingLessonRow {
  id: string
  start_time: string
  duration_minutes: number | null
  member: {
    id: string
    display_name: string
    profile_image: string | null
  }
  venue: {
    id: string
    name: string
    timezone: string
    address?: string
    images?: string[]
  }
  court: {
    id: string
    court_label: string
  }
  location?: LessonLocation
  timezone: string
}

export const getCoachIncomingLessons = async (): Promise<CoachIncomingLessonRow[]> => {
  const res = await api.get<CoachIncomingLessonRow[]>('/private_classes/coach/lessons/upcoming/')
  return res.data
}

export interface VenueLessonBooking {
  id: string
  coach: {
    id: string
    display_name: string
    profile_image: string | null
  }
  start_local: string
  end_local: string
  court_label: string
  venue_name: string
  location_type: 'venue' | 'private_court'
  location_label: string
  location?: LessonLocation
  timezone: string
}

// Venue-scoped upcoming confirmed lesson bookings for the authed member.
// Returns a bare list (matches UpcomingLessonBookingsView). Let React Query
// surface failures rather than silently hiding booked lessons.
export const getVenueUpcomingLessons = async (venue: string): Promise<VenueLessonBooking[]> => {
  const res = await api.get<VenueLessonBooking[]>(
    '/private_classes/lessons/upcoming/',
    { params: { venue } },
  )
  return res.data
}

export const getCoachUpcomingLessons = async (coachId: string): Promise<VenueLessonBooking[]> => {
  const res = await api.get<VenueLessonBooking[]>(
    `/private_classes/coaches/${coachId}/lessons/upcoming/`,
  )
  return res.data
}

// Coach profile booking — reschedule a confirmed/pending lesson booking to a
// same-priced slot. The backend returns the venue-local booked-row shape
// (UpcomingLessonBookingsSerializer) === VenueLessonBooking.
export const rescheduleLesson = async (
  bookingId: string,
  toSlotId: string,
): Promise<VenueLessonBooking> => {
  const res = await api.patch<VenueLessonBooking>(
    `/private_classes/private-lessons/bookings/${bookingId}/reschedule/`,
    { to_slot_id: toSlotId },
  )
  return res.data
}

export type RescheduleErrorCode =
  | 'reschedule_window_closed'
  | 'reschedule_not_compatible'
  | 'slot_just_taken'
  | 'no_court_available'
  | 'travel_buffer_conflict'
  | 'target_slot_not_found'

export interface LessonCancelPreviewActive {
  refund_cents: number
  refund_pct: number
  hours_before_start: number
  tier_applied: string | null
  currency: 'USD'
}
export interface LessonCancelPreviewClosed {
  closed: true
  reason: 'cancel_window_closed'
}
export interface LessonCancelPreviewAlready {
  already_cancelled: true
  refund_cents: number
}
export type LessonCancelPreview =
  | LessonCancelPreviewActive
  | LessonCancelPreviewClosed
  | LessonCancelPreviewAlready

export const getLessonCancelPreview = async (
  bookingId: string,
): Promise<LessonCancelPreview> => {
  const res = await api.get<LessonCancelPreview>(
    `/private_classes/private-lessons/bookings/${bookingId}/cancel-preview/`,
  )
  return res.data
}

export interface LessonCancelResult {
  refund_cents: number
  refund_pct: number
  tier_applied: string | null
  pi_cancelled: boolean
  reversal_ids: Record<string, string>
}

export const cancelLessonBooking = async (
  bookingId: string,
): Promise<LessonCancelResult> => {
  const res = await api.post<LessonCancelResult>(
    `/private_classes/private-lessons/bookings/${bookingId}/cancel/`,
    {},
  )
  return res.data
}
